import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Clock3, CheckCircle2 } from "lucide-react";
import { PageShell, Eyebrow } from "../components/Layout";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = ["General enquiry", "Enterprise / team access", "Partnership or co-branding", "Media / press", "Framework suggestion", "Something else"];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", topic: TOPICS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast({ title: "Enquiry received", description: "We reply within one business day, AEST." });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast({ title: "Could not send", description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <section className="hero-grid border-b border-neutral-200">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 pt-16 pb-14">
          <Eyebrow>Contact</Eyebrow>
          <h1 className="font-heading text-5xl lg:text-[60px] font-bold tracking-tight text-ink leading-[1.02]">Talk to a human.</h1>
          <p className="mt-5 text-[16px] text-ink/60 max-w-xl leading-relaxed">Enterprise access, partnerships, media, or a framework we're missing — drop us a line and we'll respond within one business day.</p>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            {done ? (
              <div className="border border-neutral-200 rounded-lg p-10 text-center" data-testid="contact-success">
                <CheckCircle2 size={44} className="text-emerald-500 mx-auto" />
                <h2 className="font-heading text-2xl font-bold text-ink mt-5">Enquiry received.</h2>
                <p className="text-[14.5px] text-ink/60 mt-2">Thanks, {form.name.split(" ")[0] || "there"} — we'll be in touch within one business day (AEST).</p>
                <button onClick={() => { setDone(false); setForm({ name: "", email: "", company: "", topic: TOPICS[0], message: "" }); }} data-testid="contact-send-another"
                  className="mt-7 border border-neutral-300 text-ink px-5 py-2.5 rounded-md text-[13.5px] font-semibold hover:border-ink transition-colors duration-200">Send another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-neutral-200 rounded-lg p-7 lg:p-9 space-y-5 bg-white" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-neutral-500 block mb-2">Full name *</label>
                    <input required value={form.name} onChange={set("name")} data-testid="contact-name-input" placeholder="Jane Citizen"
                      className="w-full border border-neutral-300 rounded-md px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-accent" />
                  </div>
                  <div>
                    <label className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-neutral-500 block mb-2">Work email *</label>
                    <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" placeholder="jane@bank.com.au"
                      className="w-full border border-neutral-300 rounded-md px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-accent" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-neutral-500 block mb-2">Company</label>
                    <input value={form.company} onChange={set("company")} data-testid="contact-company-input" placeholder="Optional"
                      className="w-full border border-neutral-300 rounded-md px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-accent" />
                  </div>
                  <div>
                    <label className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-neutral-500 block mb-2">Topic</label>
                    <select value={form.topic} onChange={set("topic")} data-testid="contact-topic-select"
                      className="w-full border border-neutral-300 rounded-md px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-accent bg-white">
                      {TOPICS.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-neutral-500 block mb-2">Message *</label>
                  <textarea required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message-input" placeholder="Tell us what you need…"
                    className="w-full border border-neutral-300 rounded-md px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-accent resize-y" />
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit-btn"
                  className="w-full bg-accent text-white rounded-md py-3.5 text-[14px] font-semibold hover:bg-accent-dark transition-colors duration-200 disabled:opacity-60">
                  {loading ? "Sending…" : "Send enquiry"}
                </button>
              </form>
            )}
          </div>
          <aside className="lg:col-span-5 space-y-5">
            {[
              { icon: Mail, h: "Email", p: "hello@riskpedia.com.au", sub: "Replies within one business day" },
              { icon: MapPin, h: "Where we work", p: "Sydney · Melbourne · Brisbane", sub: "Remote-first across Australia" },
              { icon: Clock3, h: "Hours", p: "9:00 – 17:30 AEST", sub: "Monday to Friday" },
            ].map((c) => (
              <div key={c.h} className="border border-neutral-200 rounded-lg p-6 flex gap-4">
                <c.icon size={20} className="text-accent shrink-0 mt-0.5" strokeWidth={1.8} />
                <div>
                  <p className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-neutral-400">{c.h}</p>
                  <p className="font-heading font-bold text-ink mt-1">{c.p}</p>
                  <p className="text-[12.5px] text-ink/50 mt-0.5">{c.sub}</p>
                </div>
              </div>
            ))}
            <div className="bg-ink text-white rounded-lg p-6">
              <p className="font-mono text-[10.5px] tracking-[0.25em] uppercase text-accent-light mb-2">Enterprise</p>
              <p className="text-[13.5px] text-white/70 leading-relaxed">Team seats, co-branded template libraries and regulator alert feeds for consultancies and regulated entities. Mention “Enterprise” in your message.</p>
            </div>
          </aside>
        </div>
      </section>
    </PageShell>
  );
};

export default Contact;
